import { t } from 'i18next';
import React, { FC, useEffect, useState } from 'react';

import SNavigationBloc from 'Components/structural/SNavigationBloc/SNavigationBloc';
import UButton from 'Components/unit/UButton/UButton';
import AudienceGroupList from 'Features/audience/pages/AudienceGroupList/AudienceGroupList';
import { AudienceScope, EntityType } from 'Features/audience/pages/AudienceGroupList/types';
import { getTranslatedLabel, Language } from 'Services/language/languageService';
import { AssignedAudienceGroup } from 'Store/entities/audience-group/audience-group.types';
import { getCampaigns } from 'Store/entities/activity/activity.hooks';

import { AudienceEmailList } from './AudienceEmailList/AudienceEmailList';
import styles from './AudienceForm.style';
import { AudienceMagicCode } from './AudienceMagicCode/AudienceMagicCode';
import { AudienceTargetingCampaign } from './AudienceTargetingCampaign/AudienceTargetingCampaign';

type magicCode = {
  update: (value: string) => void;
  value?: string;
  originalValue?: string;
  hasAudienceTypeChanged: boolean;
};

type segmentation = {
  update: (value: number[]) => void;
  value?: number[];
};

type emailList = {
  update: (value: { addedEmails: string[]; removedEmails: string[] }) => void;
  value?: { addedEmails: string[]; removedEmails: string[] };
  hasAudienceTypeChanged: boolean;
};

type campaign = {
  update: (value: number) => void;
  value?: number;
  originalValue?: string;
  hasAudienceTypeChanged: boolean;
};

export type AudienceFormProps = {
  entityType: EntityType;
  scope: AudienceScope;
  entityId: number;
  languages: Language[];
  assignedAudiences: AssignedAudienceGroup[];
  resetFormValues: (mode: AudienceModes) => void;
  magicCode: magicCode;
  segmentation: segmentation;
  emailList: emailList;
  campaign: campaign;
  loading?: boolean;
};

const AUDIENCE_MODE = {
  SEGMENTATION: 'SEGMENTATION',
  CAMPAIGN: 'CAMPAIGN',
  MAGIC_CODE: 'MAGIC_CODE',
  EMAIL_LIST: 'EMAIL_LIST',
} as const;

export type AudienceModes = keyof typeof AUDIENCE_MODE;

const audienceModes = Object.keys(AUDIENCE_MODE) as AudienceModes[];
const audienceTypeToAudienceMode: Record<AssignedAudienceGroup['type'], AudienceModes> = {
  all: AUDIENCE_MODE.SEGMENTATION,
  'magic-code': AUDIENCE_MODE.MAGIC_CODE,
  segmentation: AUDIENCE_MODE.SEGMENTATION,
  'email-list': AUDIENCE_MODE.EMAIL_LIST,
  audience: AUDIENCE_MODE.CAMPAIGN,
};

const getEntityAudienceMode = (assignedAudiences: AssignedAudienceGroup[]): AudienceModes | undefined => {
  if (!assignedAudiences?.length) {
    return undefined;
  }

  return audienceTypeToAudienceMode?.[assignedAudiences[0].type];
};

type NavigationBlockProps = {
  setAudienceMode: (mode: AudienceModes) => void;
  scope: AudienceScope;
  disableCampaign: boolean;
};

const tooltipId = 'audience-campaign-disabled-tooltip';

const Navigationblock: FC<NavigationBlockProps> = ({ setAudienceMode, scope, disableCampaign }) => {
  const activityName =
    scope === 'campaign' ? t('activities:audience.campaign_scope') : t('activities:audience.breaking_news_scope');

  const campaignAudienceModes =
    scope === 'campaign' ? audienceModes.filter((mode) => mode !== AUDIENCE_MODE.CAMPAIGN) : audienceModes;

  const audienceModeOptions = {
    [AUDIENCE_MODE.CAMPAIGN]: {
      title: t('activities:audience.campaign_title'),
      description: t('activities:audience.campaign_description'),
      disabled: disableCampaign,
      disabledTooltip: t('activities:audience.campaign_disabled_tooltip'),
    },
    [AUDIENCE_MODE.MAGIC_CODE]: {
      title: t('activities:audience.magic_code_title'),
      description: t('activities:audience.magic_code_description', { activity: activityName }),
      disabled: false,
    },

    [AUDIENCE_MODE.SEGMENTATION]: {
      title: t('activities:audience.audience_groups_title'),
      description: t('activities:audience.audience_groups_description'),
      disabled: false,
    },
    [AUDIENCE_MODE.EMAIL_LIST]: {
      title: t('activities:audience.email_list_title'),
      description: t('activities:audience.email_list_description'),
      disabled: false,
    },
  };

  return (
    <div style={styles.navigation}>
      <div style={styles.titlesWrapper}>
        <div style={styles.title}>{t('activities:audience.navigation_title', { activity: activityName })}</div>
        <div style={styles.subtitle}>{t('activities:audience.navigation_subtitle')}</div>
      </div>
      <div style={styles.audienceModesList}>
        {campaignAudienceModes.map((option) => {
          return (
            <div key={option}>
              <SNavigationBloc
                {...audienceModeOptions[option]}
                onClick={() => setAudienceMode(option)}
                dataTestContext={audienceModeOptions[option].title}
                data-tooltip-id={tooltipId}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const AudienceForm: FC<AudienceFormProps> = ({
  assignedAudiences,
  entityType,
  scope,
  entityId,
  languages,
  resetFormValues,
  magicCode,
  segmentation,
  emailList,
  loading,
  campaign,
}) => {
  const [audienceMode, setAudienceMode] = useState<AudienceModes | undefined>(getEntityAudienceMode(assignedAudiences));

  const [campaignOptions, setCampaignOptions] = useState<{ campaignId: number; title: string }[]>([]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      const { campaigns } = await getCampaigns();
      setCampaignOptions(
        campaigns.map((campaign) => ({
          campaignId: campaign.campaignId,
          title: getTranslatedLabel(campaign.title) || '',
        })),
      );
    };

    if (scope === 'breaking-news') {
      fetchCampaigns();
    }
  }, []);

  return (
    <div style={styles.wrapper}>
      <div style={styles.box}>
        <div style={styles.contentWrapper}>
          {!audienceMode && (
            <Navigationblock
              disableCampaign={campaignOptions.length === 0}
              scope={scope}
              setAudienceMode={(value) => {
                resetFormValues(value);
                setAudienceMode(value);
              }}
            />
          )}
          {audienceMode && (
            <UButton
              style={styles.backButton}
              onClick={() => setAudienceMode(undefined)}
              text={t('activities:audience.back_to_navigation')}
            />
          )}
          {audienceMode === AUDIENCE_MODE.SEGMENTATION && (
            <AudienceGroupList
              key="audience-group-list"
              entityType={entityType}
              scope={scope}
              entityId={entityId}
              languages={languages}
              assignedAudienceGroupIds={segmentation.value || []}
              onGroupAssignment={segmentation.update}
            />
          )}
          {audienceMode === AUDIENCE_MODE.CAMPAIGN && (
            <AudienceTargetingCampaign
              campaigns={campaignOptions}
              entityId={entityId}
              entityType={entityType}
              scope={scope}
              value={campaign.value}
              update={campaign.update}
              loading={loading}
              audienceUpdated={campaign.hasAudienceTypeChanged}
            />
          )}
          {audienceMode === AUDIENCE_MODE.MAGIC_CODE && (
            <AudienceMagicCode
              entityType={entityType}
              {...magicCode}
              entityId={entityId}
              scope={scope}
              loading={loading}
            />
          )}
          {audienceMode === AUDIENCE_MODE.EMAIL_LIST && (
            <AudienceEmailList entityType={entityType} scope={scope} entityId={entityId} {...emailList} />
          )}
        </div>
      </div>
    </div>
  );
};
